import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  SR_API_ACTION_UPDATE_COMPARATIVE_MARKET_ANALYSIS,
} from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';

import {
  createFormValidationFn,
  createInitialValues,
  validateFormFieldsAndSubmit,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrText from 'client/components/SrText';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const SrAdminComparativeMarketAnalysisForm = ({
  listing,
  dispatch,
  onSubmitForm,
}) => {
  const [buttonText, setButtonText] = useState('Save');
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);

  const cmaIsOutdated = listing.comparativeMarketAnalysisUpdated
    ? moment(listing.comparativeMarketAnalysisUpdated).isBefore(moment().subtract(6, 'months'))
    : null;

  const getFormFields = () => {
    return [
      {
        id: 'comparativeMarketAnalysisRentPrice',
        title: 'CMA Rent Price',
        type: 'currency',
        validations: ['required'],
      },
      {
        id: 'comparativeMarketAnalysisPdfUrl',
        title: 'CMA Document Upload',
        label: '(Must be .pdf format)',
        type: 'file_single',
        properties: {
          fileAccept: '.pdf',
          filePath: `/listing-uploads/comparative-market-analysis/listing-${listing.id}-{uuid}`,
          placeholder: 'Upload CMA Document',
          successMessage: 'File successfully uploaded!',
        },
        validations: ['required'],
      },
      {
        id: 'comparativeMarketAnalysisNotes',
        title: 'Notes for Partner',
        label: '(Required)',
        properties: {
          placeholder: 'Type note here...',
        },
        type: 'long_text',
        validations: ['required'],
      },
    ];
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields({
          formValues: listing,
        }),
        defaultValues: listing,
      })}
      enableReinitialize
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={(formValues) => {
        setButtonText('Saving...');
        dispatch(postWithState, {
          action: SR_API_ACTION_UPDATE_COMPARATIVE_MARKET_ANALYSIS,
          payload: {
            listingId: listing.id,
            comparativeMarketAnalysisRentPrice: formValues.comparativeMarketAnalysisRentPrice,
            comparativeMarketAnalysisPdfUrl: formValues.comparativeMarketAnalysisPdfUrl,
            comparativeMarketAnalysisNotes: formValues.comparativeMarketAnalysisNotes,
          },
          onSuccess: () => {
            setButtonText('Saved!');
            setSuccessModalIsOpen(true);
            setTimeout(() => {
              setButtonText('Save');
              setSuccessModalIsOpen(false);
            }, 3000);
            if (onSubmitForm) {
              onSubmitForm();
            }
          },
        });
      }}
    >
      {(formikBag) => {
        const { setFieldValue } = formikBag;
        const formFields = getFormFields({ setFieldValue });

        return (
          <SrCard
            header='Comparative Market Analysis (Admin-Only)'
            color={SR_CARD_BULLET_COLOR_BLUE}
            maxWidth='none'
          >
            {listing.comparativeMarketAnalysisUpdated
              && (
                <SrBox margin='15px 0 30px 0'>
                  <SrText
                    font='greycliff'
                    weight='800'
                    size='21'
                    color={cmaIsOutdated ? 'red' : 'blue300'}
                  >
                    {
                      (cmaIsOutdated ? 'OUT OF DATE: ' : '') +
                      'Last updated on ' +
                      moment(listing.comparativeMarketAnalysisUpdated).format('MM/DD/YYYY')
                    }
                  </SrText>
                </SrBox>
              )}
            <Form className='form'>
              <SrFormikField
                formField={formFields[0]}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
              <SrGridRow columns={2} maxWidth='none'>
                <SrFormikField
                  formField={formFields[1]}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
                <SrFormikField
                  formField={formFields[2]}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </SrGridRow>
              <SrBox marginTop='20px' />
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                onClick={() => validateFormFieldsAndSubmit({
                  formikBag,
                  formFields,
                })}
                disabled={!formikBag.isValid}
              >
                {buttonText}
              </SrButton>
            </Form>
            {successModalIsOpen && (
              <SrSimpleMessageModal
                iconUrl='/images/checkmark-circle-large-green.svg'
                headline='CMA Sent!'
                message='Partner has been notified.'
                maxWidth={400}
                minWidth={400}
                theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
              />
            )}
          </SrCard>
        );
      }}
    </Formik>
  );
};

SrAdminComparativeMarketAnalysisForm.propTypes = {
  listing: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func,
};

SrAdminComparativeMarketAnalysisForm.displayName = 'SrAdminComparativeMarketAnalysisForm';

export default SrAdminComparativeMarketAnalysisForm;
